/**
 * Hybrid Logical Clock. Every local write is stamped with an HLC so that
 * conflicting offline edits from different devices can be ordered
 * deterministically (last-writer-wins per field) even when device clocks
 * disagree slightly. Encoded form sorts lexicographically in causal order.
 */
export interface Hlc {
  ms: number;
  counter: number;
  node: string;
}

export class ClockDriftError extends Error {
  constructor(readonly remoteMs: number, readonly localMs: number) {
    super(`Remote clock is ${remoteMs - localMs}ms ahead of local clock`);
  }
}

const MS_WIDTH = 15;
const COUNTER_WIDTH = 5; // base36, up to 60M events per ms
const MAX_COUNTER = 36 ** COUNTER_WIDTH - 1;

export function encodeHlc(h: Hlc): string {
  return `${h.ms.toString().padStart(MS_WIDTH, '0')}:${h.counter.toString(36).padStart(COUNTER_WIDTH, '0')}:${h.node}`;
}

export function decodeHlc(s: string): Hlc {
  const [ms, counter, ...node] = s.split(':');
  if (!ms || !counter || node.length === 0) throw new Error(`Invalid HLC: ${s}`);
  return { ms: parseInt(ms, 10), counter: parseInt(counter, 36), node: node.join(':') };
}

export function compareHlc(a: Hlc, b: Hlc): number {
  return a.ms - b.ms || a.counter - b.counter || (a.node < b.node ? -1 : a.node > b.node ? 1 : 0);
}

export class HlcClock {
  private last: Hlc;

  constructor(
    readonly node: string,
    private readonly now: () => number = Date.now,
    private readonly maxDriftMs = 5 * 60_000,
  ) {
    this.last = { ms: 0, counter: 0, node };
  }

  /** Stamp a local event. */
  tick(): Hlc {
    const wall = this.now();
    if (wall > this.last.ms) {
      this.last = { ms: wall, counter: 0, node: this.node };
    } else {
      this.last = this.bump(this.last.ms, this.last.counter + 1);
    }
    return this.last;
  }

  /** Merge a timestamp received from another device/server. */
  receive(remote: Hlc): Hlc {
    const wall = this.now();
    if (remote.ms - wall > this.maxDriftMs) throw new ClockDriftError(remote.ms, wall);
    const ms = Math.max(wall, this.last.ms, remote.ms);
    let counter = 0;
    if (ms === this.last.ms && ms === remote.ms) counter = Math.max(this.last.counter, remote.counter) + 1;
    else if (ms === this.last.ms) counter = this.last.counter + 1;
    else if (ms === remote.ms) counter = remote.counter + 1;
    this.last = this.bump(ms, counter);
    return this.last;
  }

  private bump(ms: number, counter: number): Hlc {
    // Counter overflow within one ms is practically impossible; roll forward rather than wrap.
    return counter > MAX_COUNTER ? { ms: ms + 1, counter: 0, node: this.node } : { ms, counter, node: this.node };
  }
}
